import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import { ReactComponent as Calender } from '../../assets/icons/calender.svg';
import { ReactComponent as LeftArrow } from '../../assets/icons/left-arrow.svg';
import { ReactComponent as RightArrow } from '../../assets/icons/right-arrow.svg';
import { changeDateFormat } from '../../shared-functions/DataTime';
import { dateValidation } from '../../shared-functions/Validation';

interface CustomDateRangeProperties {
  inputLabel?: string;
  value: string;
  name: string;
  placeholder?: string;
  onChangeValue: (name: string, value: string) => void;
  valueRequired?: boolean;
  readOnly?: boolean;
  disablePastDates?: boolean;
  minDate?: string;
  maxDate?: string;
}

/**
 * Custom the Date Picker function
 */
function CustomDatePickers({
  inputLabel,
  name,
  value,
  placeholder,
  onChangeValue,
  valueRequired,
  readOnly,
  disablePastDates,
  minDate,
  maxDate,
}: CustomDateRangeProperties) {
  const reference = useRef<HTMLDivElement>(null);
  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  const [currentMonth, setCurrentMonth] = useState<number>(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState<number>(new Date().getFullYear());
  const [allDates, setAllDates] = useState<any[]>([]);
  const [style, setStyle] = useState<any>({});
  const [error, setError] = useState<boolean>(false);

  const [open, setOpen] = useState<boolean>(false);
  /**
   * Handle the Value change
   */
  const handleValueChange = (changeValue: string) => {
    onChangeValue(name, changeValue);
    setOpen(false);
    setError(!dateValidation(changeValue));
  };

  /**
   * Handle the custom Value change
   */
  const handleCustomValueChange = (changeValue: string) => {
    handleValueChange(changeValue);
  };

  /**
   * Handle the Input click
   */
  /**
   * Handle the Input click
   */
  const openCalenderView = (event: any) => {
    if (!readOnly) {
      setOpen(true);
      const rect = event.currentTarget.getBoundingClientRect();
      const pickerWidth = 310;
      const pickerHeight = 250;
      const margin = 10;

      // Calculate vertical position
      let top = rect.bottom + window.scrollY;
      if (rect.bottom + pickerHeight + margin > window.innerHeight + window.scrollY) {
        top = rect.top + window.scrollY - pickerHeight;
      }

      // Calculate horizontal position
      let left = rect.left + window.scrollX;
      if (rect.left + pickerWidth + margin > window.innerWidth + window.scrollX) {
        left = window.innerWidth + window.scrollX - pickerWidth - margin;
      }

      setStyle({
        top,
        left: Math.max(margin, left),
      });
    }
  };
  /**
   * Get the all days use month function
   * @param month
   * @param year
   */
  const getAllDays: (month: number, year: number) => void = (month: number, year: number) => {
    const date = new Date(year, month, 1);
    let days = [];
    while (date.getMonth() === month) {
      days.push(new Date(date));
      date.setDate(date.getDate() + 1);
    }
    days = days.map((valueChange) => {
      const formattedDate = changeDateFormat(valueChange.toString());
      let isDisabled = false;
      if (disablePastDates) {
        const today = changeDateFormat(new Date().toString());
        isDisabled = formattedDate < today;
      }
      if (minDate && !isDisabled) {
        const formattedMinDate = changeDateFormat(minDate);
        isDisabled = formattedDate < formattedMinDate;
      }
      if (maxDate && !isDisabled) {
        const formattedMaxDate = changeDateFormat(maxDate);
        isDisabled = formattedDate > formattedMaxDate;
      }
      return {
        date: formattedDate,
        number: valueChange.getDate(),
        day: valueChange.getDay(),
        isDisabled,
      };
    });
    if (days[0].day > 0) {
      const firstMonth = days[0].day;
      for (let index = 0; index < firstMonth; index += 1) {
        days = [{}, ...days];
      }
    }
    setAllDates(days);
  };

  /**
   * Handle the month change function
   * @param type
   */
  const changeMonth = (type: string) => {
    let changeValue = 0;
    let changeYear = currentYear;
    if (type === 'add') {
      changeValue = currentMonth + 1;
      if (changeValue === months.length) {
        changeValue = 0;
        changeYear = currentYear + 1;
        setCurrentYear(currentYear + 1);
      }
      getAllDays(changeValue, changeYear);

      setCurrentMonth(changeValue);
    } else {
      changeValue = currentMonth - 1;
      if (changeValue === -1) {
        changeValue = 11;
        changeYear = currentYear - 1;
        setCurrentYear(currentYear - 1);
      }
      getAllDays(changeValue, changeYear);
      setCurrentMonth(changeValue);
    }
  };

  /**
   * Handle close the calender over flow chlick function
   * @param event
   */
  const handleClickOutside = (event: MouseEvent) => {
    if (reference.current && !reference.current.contains(event.target as Node)) {
      setOpen(false);
    }
  };
  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect((): void => {
    getAllDays(currentMonth, currentYear);
  }, [minDate, maxDate, disablePastDates, currentMonth, currentYear]);

  return (
    <div className="custom-input-date-wrap">
      {inputLabel && (
        <p className="input-label">
          {inputLabel}
          {valueRequired && <span>*</span>}
        </p>
      )}
      <div className="custom-date-input">
        <div
          className={`custom-input-container ${error && 'error'}`}
          onClick={openCalenderView}
          role="presentation">
          {/* <input
            type="text"
            placeholder={placeholder}
            value={value}
            onChange={(event) => handleValueChange(event.target.value)}
            required={valueRequired}
            readOnly
          /> */}
          <input
            type="text"
            name="date"
            value={value}
            placeholder={placeholder}
            onChange={(event: any) => handleCustomValueChange(event.target.value)}
          />
          <span>
            <Calender />
          </span>
        </div>
        {open &&
          ReactDOM.createPortal(
            <div className="calender-box" ref={reference} style={style}>
              <div className="calender-header">
                <div
                  className="calender-header-left"
                  onClick={() => changeMonth('sub')}
                  role="presentation">
                  <LeftArrow />
                </div>
                <div className="calender-header-center">
                  {months[currentMonth]} - {currentYear}
                </div>
                <div
                  className="calender-header-right"
                  onClick={() => changeMonth('add')}
                  role="presentation">
                  <RightArrow />
                </div>
              </div>
              <div className="calender-body">
                <div className="calender-header-items">
                  <div className="header-item">SUN</div>
                  <div className="header-item">MON</div>
                  <div className="header-item">TUS</div>
                  <div className="header-item">WEN</div>
                  <div className="header-item">THU</div>
                  <div className="header-item">FRI</div>
                  <div className="header-item">SAT</div>
                </div>
                <div className="calender-body-items">
                  {allDates.map((dateDetail: any) =>
                    Object.keys(dateDetail).length === 0 ? (
                      <div className="header-item" />
                    ) : (
                      <div
                        className={`header-item ${dateDetail.isDisabled ? 'disabled' : ''}`}
                        onClick={() => !dateDetail.isDisabled && handleValueChange(dateDetail.date)}
                        role="presentation">
                        {dateDetail.number}
                      </div>
                    ),
                  )}
                </div>
              </div>
            </div>,
            document.body,
          )}
      </div>
    </div>
  );
}

CustomDatePickers.defaultProps = {
  inputLabel: '',
  placeholder: '',
  valueRequired: false,
  readOnly: false,
  disablePastDates: false,
  minDate: '',
  maxDate: '',
};

export default CustomDatePickers;
